import { useEffect, useState, useCallback } from 'react';

/**
 * usePwaInstallPrompt — captures the browser's beforeinstallprompt event
 * (Chrome/Edge/Android) so we can show a custom in-app install CTA at the
 * right moment.
 *
 * On iOS Safari (which has no programmatic install API) we expose
 * `isIOS = true` so the UI can show manual "Tap Share → Add to Home Screen"
 * instructions instead of a native button.
 */
export function usePwaInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferred) return null;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    setDeferred(null);
    return outcome; // 'accepted' | 'dismissed'
  }, [deferred]);

  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isIOS = /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  const isMobile = isIOS || isAndroid;
  const isStandalone =
    (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    (typeof navigator !== 'undefined' && navigator.standalone === true);

  return {
    canInstall: !!deferred,
    promptInstall,
    isIOS,
    isAndroid,
    isMobile,
    isStandalone,
    isInstalled: installed || isStandalone,
  };
}
